import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Inbox,
  InboxContent,
  PreferenceLevel,
  Preferences,
  useCounts,
  useNovu,
} from '@novu/react';
import { BellOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Grid,
  Popover,
  Select,
  Skeleton,
  Tabs,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  GetNotificationEmailFrequency,
  GetNovuSubscriberAuthSession,
  UpdateNotificationEmailFrequency,
} from '../../helper/apiHelper';
import type {
  EmailNotificationFrequency,
  NovuSubscriberAuthSession,
} from '../../Types/interfaces';
import { t } from 'i18next';
import {
  localization_de,
  localization_en,
  localization_hu,
  localization_ro,
} from './NovuLocalizations';

type NotificationSessionStatus = 'idle' | 'loading' | 'ready' | 'error';
type NotificationAppearanceFrequency = EmailNotificationFrequency | null;
type ScheduleLockState = {
  displayedFrequency: EmailNotificationFrequency;
  hasError: boolean;
  isLoading: boolean;
};

const sessionRefreshLeadTimeMs = 60 * 1000;
const notificationBootstrapSkeletonRows = [0, 1, 2];
const notificationPanelWidth = 'min(420px, calc(100vw - 32px))';
const notificationPanelHeight = 'min(545px, calc(100vh - 96px))';
const notificationPanelTabContentHeight =
  'calc(min(545px, calc(100vh - 96px)) - 42px)';
const notificationPanelWidthOnMobile = '80vw';
const notificationPanelHeightOnMobile = '70vh';
const notificationPanelTabContentHeightOnMobile = '600px';
const notificationPanelFontFamily = 'Calibri, sans-serif';
const notificationPanelFontSize = '16px';

interface NotificationCenterBellProps {
  userId?: number;
  style?: React.CSSProperties;
}

interface NovuInboxPanelProps {
  children?: React.ReactNode;
  notificationOpenLabel: string;
  onInitialized: () => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  preferencesContent: React.ReactNode;
  sessionStatus: NotificationSessionStatus;
}

type EmailFrequencyPreferenceProps = {
  onStateChange?: (state: ScheduleLockState) => void;
};

type NotificationPreferencesPanelProps = {
  onStateChange?: (state: ScheduleLockState) => void;
  scheduleLockState: ScheduleLockState;
};

const emailFrequencyOptions: Array<{
  label: string;
  value: EmailNotificationFrequency;
}> = [
  { label: t('header.notifications.instant'), value: 'instant' },
  { label: t('header.notifications.daily'), value: 'daily' },
];

const visuallyHiddenStyle: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export const buildNotificationAppearance = (
  frequency: NotificationAppearanceFrequency,
  options?: {
    hasError?: boolean;
    isLoading?: boolean;
  }
) => {
  const isScheduleLocked =
    !options?.isLoading && !options?.hasError && frequency === 'instant';

  return {
    variables: {
      fontSize: notificationPanelFontSize,
      colorPrimary: '#066D99',
      colorForeground: '#172033',
      colorBackground: '#ffffff',
      colorCounter: '#ff4d4f',
      colorCounterForeground: '#ffffff',
    },
    elements: {
      inboxContent: {
        minHeight: '100%',
        fontFamily: notificationPanelFontFamily,
        fontSize: notificationPanelFontSize,
      },
      notificationListContainer: {
        height: '100%',
        overflowY: 'visible',
      },
      notificationList: {
        overflowY: 'visible',
      },
      notificationListEmptyNoticeContainer: {
        padding: '24px 16px',
      },
      notification: {
        fontFamily: notificationPanelFontFamily,
        fontSize: notificationPanelFontSize,
      },
      notificationSubject: {
        fontFamily: notificationPanelFontFamily,
        fontSize: notificationPanelFontSize,
      },
      notificationBody: {
        fontFamily: notificationPanelFontFamily,
        fontSize: notificationPanelFontSize,
      },
      notificationDate: {
        fontFamily: notificationPanelFontFamily,
        fontSize: notificationPanelFontSize,
      },
      inboxStatus__title: {
        fontFamily: notificationPanelFontFamily,
        fontSize: notificationPanelFontSize,
      },
      inboxStatus__dropdownTrigger: {
        fontFamily: notificationPanelFontFamily,
        fontSize: notificationPanelFontSize,
      },
      inboxStatus__dropdownItemLabel: {
        fontFamily: notificationPanelFontFamily,
        fontSize: notificationPanelFontSize,
      },
      preferencesContainer: {
        minHeight: 'auto',
        paddingBottom: 0,
      },
      preferencesHeader__title: {
        display: 'none',
        fontFamily: notificationPanelFontFamily,
        fontSize: notificationPanelFontSize,
      },
      preferencesGroupLabel: {
        fontFamily: notificationPanelFontFamily,
        fontSize: notificationPanelFontSize,
      },
      workflowContainer: ({
        preference,
      }: {
        preference: { level: PreferenceLevel };
      }): string =>
        preference.level === PreferenceLevel.GLOBAL ? '' : 'nt-hidden',
      workflowLabel: {
        fontFamily: notificationPanelFontFamily,
        fontSize: notificationPanelFontSize,
      },
      workflowDescription: {
        fontFamily: notificationPanelFontFamily,
        fontSize: notificationPanelFontSize,
      },
      channelLabel: {
        fontFamily: notificationPanelFontFamily,
        fontSize: notificationPanelFontSize,
      },
      channelSwitch: {
        alignItems: 'center',
        display: 'inline-flex',
      },
      channelSwitchThumb: {
        top: '50%',
        transform: 'translateY(-50%)',
      },
      channelName: {
        fontFamily: notificationPanelFontFamily,
        fontSize: notificationPanelFontSize,
      },
      scheduleLabel: {
        fontFamily: notificationPanelFontFamily,
        fontSize: notificationPanelFontSize,
        opacity: isScheduleLocked ? 0.55 : 1,
      },
      scheduleDescription: {
        fontFamily: notificationPanelFontFamily,
        fontSize: notificationPanelFontSize,
        opacity: isScheduleLocked ? 0.55 : 1,
      },
      scheduleActionsContainer: {
        alignItems: 'center',
        display: 'flex',
        pointerEvents: isScheduleLocked ? 'none' : 'auto',
        opacity: isScheduleLocked ? 0.45 : 1,
      },
      scheduleBodyColumn: {
        alignItems: 'center',
        opacity: isScheduleLocked ? 0.7 : 1,
      },
      skeletonSwitch: {
        alignItems: 'center',
      },
      skeletonSwitchThumb: {
        top: '50%',
        transform: 'translateY(-50%)',
      },
    },
  };
};

const NotificationPanelSkeleton = (): React.ReactNode => (
  <div
    className="notification-bootstrap-skeleton"
    role="presentation"
    aria-hidden="true"
    style={{ position: 'static', width: '100%' }}
  >
    <div className="notification-bootstrap-skeleton__shell">
      <div className="notification-bootstrap-skeleton__header">
        <Skeleton.Button
          active
          size="small"
          shape="round"
          className="notification-bootstrap-skeleton__title"
        />
        <Skeleton.Button
          active
          size="small"
          shape="circle"
          className="notification-bootstrap-skeleton__header-action"
        />
      </div>

      <div className="notification-bootstrap-skeleton__content">
        <div className="notification-bootstrap-skeleton__list">
          {notificationBootstrapSkeletonRows.map((rowIndex) => (
            <div
              key={rowIndex}
              className="notification-bootstrap-skeleton__item"
            >
              <Skeleton.Avatar
                active
                size={40}
                shape="circle"
                className="notification-bootstrap-skeleton__avatar"
              />
              <div className="notification-bootstrap-skeleton__item-content">
                <Skeleton.Input
                  active
                  size="small"
                  className="notification-bootstrap-skeleton__line notification-bootstrap-skeleton__line--primary"
                />
                <Skeleton.Input
                  active
                  size="small"
                  className="notification-bootstrap-skeleton__line notification-bootstrap-skeleton__line--secondary"
                />
              </div>
              <Skeleton.Input
                active
                size="small"
                className="notification-bootstrap-skeleton__timestamp"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="notification-bootstrap-skeleton__footer">
        <Skeleton.Input
          active
          size="small"
          className="notification-bootstrap-skeleton__footer-prefix"
        />
        <Skeleton.Input
          active
          size="small"
          className="notification-bootstrap-skeleton__footer-logo"
        />
      </div>
    </div>
  </div>
);

const EmailFrequencyPreference = ({
  onStateChange,
}: EmailFrequencyPreferenceProps): React.ReactNode => {
  const { t } = useTranslation();
  const [frequency, setFrequency] =
    useState<EmailNotificationFrequency>('instant');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function loadFrequency(): Promise<void> {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const response = await GetNotificationEmailFrequency();
        if (isMounted) {
          setFrequency(response.frequency);
        }
      } catch (loadError) {
        console.error(loadError);
        if (isMounted) {
          setErrorMessage(t('header.notifications.frequencyLoadError'));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    void loadFrequency();

    return (): void => {
      isMounted = false;
    };
  }, [t]);

  useEffect(() => {
    onStateChange?.({
      displayedFrequency: frequency,
      hasError: Boolean(errorMessage),
      isLoading,
    });
  }, [errorMessage, frequency, isLoading, onStateChange]);

  const handleFrequencyChange = async (
    nextFrequency: EmailNotificationFrequency
  ): Promise<void> => {
    const previousFrequency = frequency;
    setFrequency(nextFrequency);
    setIsSaving(true);
    setErrorMessage(null);

    try {
      const response = await UpdateNotificationEmailFrequency(nextFrequency);
      setFrequency(response.frequency);
    } catch (saveError) {
      console.error(saveError);
      setFrequency(previousFrequency);
      setErrorMessage(t('header.notifications.frequencySaveError'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      style={{
        borderBottom: '1px solid #f0f0f0',
        padding: '12px 16px',
      }}
    >
      <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
        {t('header.notifications.emailFrequency')}
      </Typography.Text>
      <Select<EmailNotificationFrequency>
        aria-label={t('header.notifications.emailFrequency')}
        disabled={isLoading || isSaving}
        loading={isLoading || isSaving}
        onChange={(value) => {
          void handleFrequencyChange(value);
        }}
        options={emailFrequencyOptions}
        size="middle"
        value={frequency}
        style={{ width: '100%' }}
      />
      {errorMessage ? (
        <Typography.Text
          type="secondary"
          style={{ display: 'block', fontSize: 12, marginTop: 6 }}
        >
          {errorMessage}
        </Typography.Text>
      ) : null}
    </div>
  );
};

export const NotificationPreferencesPanel = ({
  onStateChange,
  scheduleLockState,
}: NotificationPreferencesPanelProps): React.ReactNode => {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const isScheduleLocked =
    !scheduleLockState.isLoading &&
    !scheduleLockState.hasError &&
    scheduleLockState.displayedFrequency === 'instant';

  return (
    <div
      style={{
        height: screens.lg
          ? notificationPanelTabContentHeight
          : notificationPanelTabContentHeightOnMobile,
        overflowY: 'auto',
      }}
    >
      <EmailFrequencyPreference onStateChange={onStateChange} />
      <div
        aria-disabled={isScheduleLocked}
        data-schedule-locked={isScheduleLocked ? 'true' : 'false'}
        data-testid="novu-preferences-panel"
        style={{ paddingBottom: 12 }}
      >
        {isScheduleLocked ? (
          <Typography.Text
            data-testid="novu-schedule-locked-message"
            type="secondary"
            style={{
              display: 'block',
              padding: '12px 16px 0',
              fontSize: 12,
            }}
          >
            {t('header.notifications.instantScheduleLocked')}
          </Typography.Text>
        ) : null}
        <Preferences />
      </div>
    </div>
  );
};

const NovuLiveRefreshBridge = ({
  onNotificationReceived,
}: {
  onNotificationReceived: () => void;
}): React.ReactNode => {
  const novu = useNovu();

  useEffect(() => {
    return novu.on('notifications.notification_received', () => {
      onNotificationReceived();
    });
  }, [novu, onNotificationReceived]);

  return null;
};

const NovuInboxPanel = ({
  children,
  notificationOpenLabel,
  onInitialized,
  onOpenChange,
  open,
  preferencesContent,
  sessionStatus,
}: NovuInboxPanelProps): React.ReactNode => {
  const { counts } = useCounts({
    filters: [{ read: false }],
  });
  const unreadCount = counts?.[0]?.count ?? 0;
  const screens = Grid.useBreakpoint();

  useEffect(() => {
    onInitialized();
  }, [onInitialized]);

  const panelContent = (
    <div
      style={{
        width: '100%',
        maxWidth: '100%',
        height: notificationPanelHeight,
        fontFamily: notificationPanelFontFamily,
        overflow: 'hidden',
      }}
    >
      <Tabs
        defaultActiveKey="notifications"
        size="small"
        style={{ height: '100%' }}
        tabBarStyle={{
          paddingLeft: 20,
          marginBottom: 0,
          fontFamily: notificationPanelFontFamily,
        }}
        items={[
          {
            key: 'notifications',
            label: t('header.notifications.notifications'),
            children: (
              <div
                style={{
                  height: screens.lg
                    ? notificationPanelTabContentHeight
                    : notificationPanelTabContentHeightOnMobile,
                  overflowY: 'auto',
                }}
              >
                <InboxContent hideNav />
              </div>
            ),
          },
          {
            key: 'preferences',
            label: t('header.notifications.preferences'),
            children: preferencesContent,
          },
        ]}
      />
    </div>
  );

  return (
    <Popover
      content={panelContent}
      open={open}
      onOpenChange={onOpenChange}
      placement="bottomRight"
      trigger="click"
      arrow={false}
      overlayInnerStyle={{
        padding: 0,
        width: screens.lg
          ? notificationPanelWidth
          : notificationPanelWidthOnMobile,
        height: screens.lg
          ? notificationPanelHeight
          : notificationPanelHeightOnMobile,
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        aria-label={`${notificationOpenLabel}${
          unreadCount
            ? `. ${unreadCount}${t('header.notifications.unreadNotifications')}`
            : ''
        }`}
        disabled={sessionStatus === 'loading'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: sessionStatus === 'loading' ? 'default' : 'pointer',
        }}
      >
        <span
          style={{
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '30px',
            height: '30px',
            color: 'white',
          }}
        >
          <BellOutlined style={{ fontSize: '20px' }} />
          {unreadCount > 0 ? (
            <span
              aria-hidden="true"
              data-testid="notification-unread-dot"
              style={{
                position: 'absolute',
                top: '-8%',
                left: 0,
                width: '10px',
                height: '10px',
                borderRadius: '9999px',
                backgroundColor: '#ff4d4f',
                border: '2px solid #066D99',
                boxSizing: 'border-box',
              }}
            />
          ) : null}
        </span>
      </button>
      {children}
    </Popover>
  );
};

const NotificationCenterBell = ({
  userId,
  style,
}: NotificationCenterBellProps): React.ReactNode => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [sessionStatus, setSessionStatus] =
    useState<NotificationSessionStatus>('idle');
  const [sessionData, setSessionData] =
    useState<NovuSubscriberAuthSession | null>(null);
  const [isNovuSessionInitialized, setIsNovuSessionInitialized] =
    useState(false);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [providerRefreshNonce, setProviderRefreshNonce] = useState(0);
  const [scheduleLockState, setScheduleLockState] = useState<ScheduleLockState>(
    {
      displayedFrequency: 'instant',
      hasError: false,
      isLoading: true,
    }
  );
  const sessionRequestRef = useRef<Promise<void> | null>(null);
  const screens = Grid.useBreakpoint();
  const prevUserIdRef = useRef(userId);
  const prevPathnameRef = useRef(location.pathname);
  const expiresAtRef = useRef<number | null>(null);
  const shouldInitializeRef = useRef(true);
  const pendingOpenRef = useRef(false);
  const panelOpenRef = useRef(false);

  const novuApplicationIdentifier =
    process.env.REACT_APP_NOVU_APPLICATION_IDENTIFIER || '';
  const novuBackendUrl = process.env.REACT_APP_NOVU_API_URL || undefined;
  const novuSocketUrl = process.env.REACT_APP_NOVU_SOCKET_URL || undefined;
  const novuProviderInstanceKey = useMemo(
    () =>
      `${sessionData?.subscriberId ?? 'anonymous'}-${
        sessionData?.issuedAtUtc ?? 'no-session'
      }-${providerRefreshNonce}`,
    [providerRefreshNonce, sessionData?.issuedAtUtc, sessionData?.subscriberId]
  );

  // Sync refs with state
  useEffect(() => {
    pendingOpenRef.current = pendingOpen;
  }, [pendingOpen]);

  useEffect(() => {
    panelOpenRef.current = panelOpen;
  }, [panelOpen]);

  // Reset state when userId or path changes
  useEffect(() => {
    if (
      userId !== prevUserIdRef.current ||
      location.pathname !== prevPathnameRef.current
    ) {
      prevUserIdRef.current = userId;
      prevPathnameRef.current = location.pathname;
      setSessionStatus('idle');
      setSessionData(null);
      setErrorMessage(null);
      setPendingOpen(false);
      setPanelOpen(false);
      setIsNovuSessionInitialized(false);
      sessionRequestRef.current = null;
      expiresAtRef.current = null;
      shouldInitializeRef.current = true;
    }
  }, [location.pathname, userId]);

  // Update expiration ref when session data changes
  useEffect(() => {
    if (sessionData?.expiresAtUtc) {
      const parsed = Date.parse(sessionData.expiresAtUtc);
      expiresAtRef.current = isNaN(parsed) ? null : parsed;
    } else {
      expiresAtRef.current = null;
    }
  }, [sessionData?.expiresAtUtc]);

  const notificationRegionLabel = t('header.notifications.regionLabel');
  const notificationOpenLabel = t('header.notifications.openBell');
  const notificationReadyLabel = t('header.notifications.ready');
  const notificationLoadingLabel = t('header.notifications.loading');
  const notificationErrorLabel = t('header.notifications.error');
  const notificationRetryLabel = t('header.notifications.retry');
  const notificationBootstrappingLabel = t(
    'header.notifications.bootstrapping'
  );

  const fetchSubscriberSession = useCallback(async () => {
    if (!userId) {
      return;
    }

    if (sessionRequestRef.current) {
      return sessionRequestRef.current;
    }

    if (!novuApplicationIdentifier) {
      setSessionStatus('error');
      setErrorMessage(t('header.notifications.configMissing'));
      return;
    }

    const sessionRequest = (async (): Promise<void> => {
      setSessionStatus('loading');
      setErrorMessage(null);
      setIsNovuSessionInitialized(false);

      try {
        const response = await GetNovuSubscriberAuthSession();
        setSessionData(response);
        setSessionStatus('ready');
      } catch (sessionError) {
        console.error(sessionError);
        setSessionStatus('error');
        setSessionData(null);
        setPendingOpen(false);
        setPanelOpen(false);
        setErrorMessage(t('header.notifications.sessionError'));
      } finally {
        sessionRequestRef.current = null;
      }
    })();

    sessionRequestRef.current = sessionRequest;
    await sessionRequest;
  }, [novuApplicationIdentifier, t, userId]);

  // Handle initialization - only on mount or when reset
  const initializeSession = useCallback(() => {
    if (userId && novuApplicationIdentifier && shouldInitializeRef.current) {
      shouldInitializeRef.current = false;
      void fetchSubscriberSession();
    }
  }, [fetchSubscriberSession, novuApplicationIdentifier, userId]);

  // Call initialization when component is ready
  useEffect(() => {
    initializeSession();
  }, [initializeSession]);

  // Refresh session before expiration
  useEffect(() => {
    if (sessionStatus !== 'ready' || !expiresAtRef.current || !userId) {
      return;
    }

    const refreshDelayMs =
      expiresAtRef.current - Date.now() - sessionRefreshLeadTimeMs;

    if (refreshDelayMs <= 0) {
      return;
    }

    const refreshTimer = window.setTimeout(() => {
      shouldInitializeRef.current = true;
      void fetchSubscriberSession();
    }, refreshDelayMs);

    return (): void => {
      window.clearTimeout(refreshTimer);
    };
  }, [fetchSubscriberSession, sessionStatus, userId]);

  const bootstrapAndOpen = useCallback(async () => {
    setPendingOpen(true);
    await fetchSubscriberSession();
  }, [fetchSubscriberSession]);

  const handlePanelOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setPanelOpen(false);
        return;
      }

      // Check if session is ready and not expiring
      const isExpiring = expiresAtRef.current
        ? expiresAtRef.current <= Date.now() + sessionRefreshLeadTimeMs
        : false;

      if (sessionStatus !== 'ready' || !sessionData || isExpiring) {
        void bootstrapAndOpen();
        return;
      }

      setPanelOpen(true);
    },
    [bootstrapAndOpen, sessionData, sessionStatus]
  );

  const handlePanelReady = useCallback(() => {
    setIsNovuSessionInitialized(true);
  }, []);

  const handleLiveNotificationReceived = useCallback(() => {
    setPendingOpen(
      (currentPendingOpen) => currentPendingOpen || panelOpenRef.current
    );
    setPanelOpen(false);
    setIsNovuSessionInitialized(false);
    setProviderRefreshNonce((currentNonce) => currentNonce + 1);
  }, []);

  const GetLanguage = useCallback(() => {
    switch (i18n.language.split('-')[0]) {
      case 'en':
        return localization_en;
      case 'hu':
        return localization_hu;
      case 'ro':
        return localization_ro;
      case 'de':
        return localization_de;
      default:
        return localization_en;
    }
  }, [i18n.language]);

  const preferencesContent = (
    <NotificationPreferencesPanel
      onStateChange={setScheduleLockState}
      scheduleLockState={scheduleLockState}
    />
  );

  const renderFallbackBellButton = (): React.ReactNode => (
    <button
      type="button"
      aria-label={notificationOpenLabel}
      onClick={async (event) => {
        event.preventDefault();
        await bootstrapAndOpen();
      }}
      disabled={pendingOpen}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        border: 'none',
        padding: 0,
        cursor: pendingOpen ? 'default' : 'pointer',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '30px',
          height: '30px',
          color: 'white',
        }}
      >
        <BellOutlined style={{ fontSize: '20px' }} />
      </span>
    </button>
  );

  const renderLoadingBellButton = (): React.ReactNode => (
    <Popover
      content={<NotificationPanelSkeleton />}
      open={pendingOpen}
      placement="bottomRight"
      trigger="click"
      arrow={false}
      overlayInnerStyle={{
        padding: 0,
        width: screens.lg
          ? notificationPanelWidth
          : notificationPanelWidthOnMobile,
        height: screens.lg
          ? notificationPanelHeight
          : notificationPanelHeightOnMobile,
        overflow: 'hidden',
      }}
    >
      {renderFallbackBellButton()}
    </Popover>
  );

  if (!userId) {
    return null;
  }

  return (
    <div
      role="group"
      aria-label={notificationRegionLabel}
      aria-busy={sessionStatus === 'loading' || !isNovuSessionInitialized}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        position: 'relative',
        gap: '8px',
        flexShrink: 0,
        ...style,
      }}
    >
      <span style={visuallyHiddenStyle} role="status" aria-live="polite">
        {sessionStatus === 'error'
          ? notificationErrorLabel
          : isNovuSessionInitialized
            ? notificationReadyLabel
            : sessionStatus === 'loading'
              ? notificationBootstrappingLabel
              : notificationLoadingLabel}
      </span>

      {sessionStatus === 'ready' && sessionData ? (
        <Inbox
          localization={{
            locale: i18n.language,
            ...GetLanguage(),
          }}
          key={novuProviderInstanceKey}
          backendUrl={novuBackendUrl}
          socketUrl={novuSocketUrl}
          subscriber={sessionData.subscriberId}
          subscriberHash={sessionData.signature}
          applicationIdentifier={novuApplicationIdentifier}
          appearance={buildNotificationAppearance(
            scheduleLockState.displayedFrequency,
            {
              hasError: scheduleLockState.hasError,
              isLoading: scheduleLockState.isLoading,
            }
          )}
        >
          <NovuInboxPanel
            notificationOpenLabel={notificationOpenLabel}
            onInitialized={handlePanelReady}
            onOpenChange={handlePanelOpenChange}
            open={panelOpen}
            preferencesContent={preferencesContent}
            sessionStatus={sessionStatus}
          >
            <NovuLiveRefreshBridge
              onNotificationReceived={handleLiveNotificationReceived}
            />
          </NovuInboxPanel>
        </Inbox>
      ) : sessionStatus === 'loading' || pendingOpen ? (
        renderLoadingBellButton()
      ) : (
        renderFallbackBellButton()
      )}

      {sessionStatus === 'error' ? (
        <div
          role="alert"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            maxWidth: '240px',
          }}
        >
          <Typography.Text type="danger" style={{ fontSize: '12px' }}>
            <ExclamationCircleOutlined style={{ marginRight: '4px' }} />
            {errorMessage ?? notificationErrorLabel}
          </Typography.Text>
          <Button
            size="small"
            type="link"
            onClick={() => {
              shouldInitializeRef.current = true;
              void fetchSubscriberSession();
            }}
            style={{ paddingInline: 0 }}
          >
            {notificationRetryLabel}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default NotificationCenterBell;
